import React, { createContext, useContext, useState, useEffect } from 'react'
import { useGetConfigurationQuery } from '../services/api'
import { useOwner } from './OwnerContext'

export type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>('light')
  const { isOwnerValid } = useOwner()

  // Buscar configurações da API para obter o tema
  const { data: configData } = useGetConfigurationQuery(undefined, {
    skip: !isOwnerValid,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  // Carregar tema das configurações da API
  useEffect(() => {
    if (configData?.success && configData.data) {
      const dataAny: any = configData.data

      // Tentar obter tema em formato camelCase ou snake_case
      const appearanceSettingsCamel = dataAny.appearanceSettings
      const appearanceSettingsSnake = dataAny.appearance_settings

      if (appearanceSettingsCamel?.theme) {
        const apiTheme = appearanceSettingsCamel.theme as Theme
        setTheme(apiTheme)
        localStorage.setItem('lognex-theme', apiTheme)
        document.documentElement.setAttribute('data-theme', apiTheme)
      } else if (appearanceSettingsSnake?.theme) {
        const apiTheme = appearanceSettingsSnake.theme as Theme
        setTheme(apiTheme)
        localStorage.setItem('lognex-theme', apiTheme)
        document.documentElement.setAttribute('data-theme', apiTheme)
      } else {
        // Fallback para localStorage se não houver tema nas configurações
        const savedTheme = localStorage.getItem('lognex-theme') as Theme
        if (savedTheme) {
          setTheme(savedTheme)
          document.documentElement.setAttribute('data-theme', savedTheme)
        }
      }
    } else {
      // Fallback para localStorage se não conseguir carregar configurações
      const savedTheme = localStorage.getItem('lognex-theme') as Theme
      if (savedTheme) {
        setTheme(savedTheme)
        document.documentElement.setAttribute('data-theme', savedTheme)
      }
    }
  }, [configData])

  // Salvar tema ao localStorage quando mudar (além de já ter sido feito acima)
  useEffect(() => {
    localStorage.setItem('lognex-theme', theme)
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
